import { Box, HStack, Text, VStack } from "@chakra-ui/react"
import { BLOCK_HASH_PREFIX } from "@pathwright/blocks/src/blocksLinks/constants"
import { navigateToBlockElement } from "@pathwright/blocks/src/blocksLinks/functions"
import type { IconName } from "@pathwright/pathicons"
import Link from "@pathwright/ux/src/components/link/Link"
import { MouseEvent, ReactNode, useEffect, useRef } from "react"
import {
  CohortSyncPathCohortFragment,
  PathItemFragment,
  ResourceFragment
} from "@pathwright/graphql-client/src/generated"
import { getStepVerbIcon } from "../../path/utils"
import Pathicon from "../../pathicon/Pathicon"
import PWAdminHint from "../../pw-admin/PWAdminHint"
import { getGroupUrl, getStepUrl } from "../../utils/urls"
import { LEARN } from "../constants"
import { usePathNavigator } from "./PathNavigatorContext"
import Highlight from "./PathNavigatorHighlight"
import { PathNavigatorUnarchiveItem } from "./PathNavigatorUnarchive"
import { getIsPathItem } from "./search"
import {
  NavigatorItem,
  SearchableBlockType,
  SearchablePathItem,
  Snippets
} from "./types"

const getCompletionStatusIcon = (
  item: PathItemFragment,
  items: Array<PathItemFragment>
) => {
  const isComplete = !!item.completion?.is_complete

  if (isComplete) return "check-circle"

  const firstIncompleteItem = items.find(
    (i: PathItemFragment) => i.type === "step" && !i.completion?.is_complete
  )

  return firstIncompleteItem?.id === item.id
    ? "chevron-right-circle-fill"
    : "circle"
}

const PathNavigatorItem = ({
  item,
  isFocused
}: {
  item: NavigatorItem
  isFocused: boolean
}) => {
  const {
    cohort,
    resource,
    onClose,
    pathMode,
    items,
    setHighlightStyle,
    isMobile,
    handleNavigateToItem,
    handleLogEvent,
    search,
    currentItem,
    getResultURL,
    getResultProps
  } = usePathNavigator()

  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!itemRef.current) return

    if (isFocused) {
      itemRef.current.focus()
      itemRef.current.scrollIntoView({ block: "nearest" })
    }
  }, [isFocused])

  if (item.type === "divider") return null

  const getItemUrl = (
    item: PathItemFragment,
    resource: ResourceFragment,
    cohort: CohortSyncPathCohortFragment
  ) => {
    if (getResultURL) {
      return getResultURL(item as PathItemFragment)
    }

    const urlMap = {
      lesson: () =>
        `${getGroupUrl(resource.slug, cohort!.id)}#lesson-${item.source_id}`,
      step: () => getStepUrl(resource.slug, cohort!.id, item.source_id),
      resource: () => {
        let url = ""
        if (item.linked_cohort && item.linked_resource) {
          url = getGroupUrl(item.linked_resource.slug, item.linked_cohort.id)
        }
        return url
      }
    } as {
      [key: string]: () => string
    }

    return urlMap[item.type]?.() || ""
  }

  let itemUrl = getItemUrl(item as PathItemFragment, resource, cohort)

  const handleHover = (
    event: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>
  ) => {
    const container = event.currentTarget.closest(
      "[data-path-navigator-items='true']"
    ) as HTMLDivElement | null
    if (!container) return

    const itemRect = event.currentTarget.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    // Adjust for scrolling inside the container
    const scrollOffset = container.scrollTop

    setHighlightStyle({
      transform: `translateY(${
        itemRect.top - containerRect.top + scrollOffset
      }px)`,
      width: `${itemRect.width}px`,
      height: `${itemRect.height}px`,
      opacity: 1
    })
  }

  const parseSnippet = (htmlSnippet: string, type: "item" | "block") => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlSnippet, "text/html")

    let keyCounter = 0 // Ensure unique keys

    const processNode = (node: Node, index: number): ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) {
        return (
          <span key={`text-${index}-${keyCounter++}`}>{node.textContent}</span>
        )
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement

        if (element.tagName === "MARK") {
          return (
            <Box
              as="mark"
              key={`mark-${index}-${keyCounter++}`}
              fontWeight="bold"
              padding="0 2px"
              borderRadius="sm"
              color={type === "block" ? "whiteAlpha.700" : "whiteAlpha.900"}
              backgroundColor={
                type === "block" ? "transparent" : "whiteAlpha.300"
              }
            >
              {element.textContent}
            </Box>
          )
        }

        // Preserve line breaks for block-level elements
        const children = Array.from(element.childNodes).map(
          (child, childIndex) => processNode(child, childIndex)
        )

        return (
          <span key={`snippet-${index}-${keyCounter++}`}>
            {children}
            {["P", "H1", "H2", "H3", "H4", "H5", "H6"].includes(
              element.tagName
            ) && <br />}
          </span>
        )
      }
      return null
    }

    return Array.from(doc.body.childNodes).map((node, index) =>
      processNode(node, index)
    )
  }

  const renderItem = (item: SearchablePathItem) => {
    const snippets = item.searchResult?.snippets || {}

    const linkProps = getResultProps?.(item as PathItemFragment) || {}

    return (
      <Link
        variant="plain"
        to={itemUrl ?? undefined}
        id={`navigator-item-${item.id}`}
        whiteSpace="normal"
        onClick={(event) => {
          event.preventDefault()

          if (!getIsPathItem(item)) return

          // Handle navigation manually so we can use consistent behavior with Enter key
          handleNavigateToItem(item)
        }}
        cursor={itemUrl ? "pointer" : "default"}
        tabIndex={0}
        display="flex"
        justifyContent="start"
        width="100%"
        px={2}
        py="3px"
        mt={item.type === "lesson" ? 2 : 0}
        mb={item.type === "lesson" ? 1 : 0}
        onMouseEnter={handleHover}
        position="relative"
        {...linkProps}
      >
        {isFocused ? (
          <Highlight
            staticStyle={{
              width: "100%",
              height: "100%",
              opacity: "1",
              backgroundColor: "whiteAlpha.300"
            }}
          />
        ) : null}
        <HStack
          justify="start"
          w="100%"
          color={isFocused ? "white" : "whiteAlpha.900"}
          _hover={{
            color: "white"
          }}
        >
          {item.type === "lesson" ? (
            <Text
              fontSize="lg"
              as="strong"
              fontWeight="extrabold"
              m={0}
              ref={itemRef}
              color={linkProps?.color || "white"}
            >
              {snippets.title
                ? parseSnippet(snippets.title, "item")
                : item.name}
            </Text>
          ) : item.type === "step" ? (
            <VStack w="100%" align="start" gap={0}>
              <HStack w="100%" ref={itemRef} align="center">
                <Pathicon
                  icon={
                    (pathMode === LEARN
                      ? getCompletionStatusIcon(
                          item,
                          items as PathItemFragment[]
                        )
                      : getStepVerbIcon(item.verb!)) as IconName
                  }
                  style={{
                    marginRight: "5px"
                  }}
                />
                <Text
                  fontSize="md"
                  whiteSpace="pre-wrap"
                  overflowWrap="break-word"
                  m={0}
                  textAlign="left"
                  color="white"
                  fontWeight="semibold"
                >
                  {snippets.title
                    ? parseSnippet(snippets.title, "item")
                    : item.name}
                </Text>
              </HStack>
              {snippets.description ? (
                <Text
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                  overflowWrap="break-word"
                  m={0}
                  color="whiteAlpha.500"
                  textAlign="left"
                  pl={0}
                  fontFamily="monospace"
                >
                  {parseSnippet(snippets.description, "item")}
                </Text>
              ) : null}
            </VStack>
          ) : null}
        </HStack>
        <PathNavigatorUnarchiveItem item={item} />
      </Link>
    )
  }

  const renderBlock = (item: SearchableBlockType) => {
    // The searchResult contains the matched snippets that we need to display for Blocks
    if (!item?.searchResult?.snippets) return null

    const pathItem = items.find((pathItem: NavigatorItem) => {
      return (
        getIsPathItem(pathItem) && `/${pathItem.source_id}` === item.contextKey
      )
    }) as SearchablePathItem

    // We need to pre-parse the snippets so we can determine whether to show the container
    const parsedSnippets = Object.keys(item.searchResult!.snippets as Snippets)
      .map((field: string) => {
        const snippet = item.searchResult!.snippets![field]
        const parsed = parseSnippet(snippet, "block")
        return parsed?.length ? { snippet, field } : null
      })
      .filter((result) => result !== null) as Array<{
      snippet: ReactNode
      field: string
    }>

    if (!parsedSnippets.length) return null

    const blockElementID = `${BLOCK_HASH_PREFIX}${item.id}`
    const isCurrentStepBlock =
      currentItem?.type === "step" &&
      pathItem?.type === "step" &&
      currentItem.source_id === pathItem.source_id

    return (
      <VStack
        w="100%"
        align="start"
        pl={"39px"}
        // For some reason, PL is being overridden by padding-inline-start
        style={{ paddingInlineStart: "39px" }}
        my={0}
        py={0}
        px={0}
        gap={0}
      >
        {parsedSnippets.map(
          (
            { snippet, field }: { snippet: ReactNode; field: string },
            index: number
          ) => {
            return (
              <Link
                key={`item-${item.id}-snippet-${index}`}
                variant="plain"
                to={getBlockUrl(pathItem) ?? undefined}
                w="100%"
                color="white"
                display="flex"
                justifyContent="start"
                onClick={(event) => {
                  handleLogEvent("Navigator: click Blocks match")
                  if (search)
                    handleLogEvent("Navigator: click Blocks after search")

                  const isModifiedClick =
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.button !== 0

                  // Clicking a Block result in same route doesn't work by default,
                  // since the Blocks link functionality looks for route changes,
                  // so we handle the click manually.
                  // We ignore modified clicks (Ctrl+click, Cmd+click, etc.)
                  // since we want to allow those to open the block in a new tab, etc.
                  if (isCurrentStepBlock && !isModifiedClick) {
                    event.preventDefault()
                    navigateToBlockElement(blockElementID)
                  }

                  // On mobile, close the overlay when clicking a matched block link
                  if (isMobile) onClose()
                }}
              >
                <Box
                  as="small"
                  fontSize={field === "title" ? "0.8rem" : "0.7rem"}
                  fontWeight={field === "title" ? "700" : "normal"}
                  color="whiteAlpha.500"
                  fontFamily="monospace"
                  css={{
                    "& mark": {
                      fontWeight: "bold",
                      borderRadius: "sm",
                      color: "whiteAlpha.800",
                      backgroundColor: "transparent"
                    }
                  }}
                  dangerouslySetInnerHTML={{
                    __html: snippet as string
                  }}
                />
              </Link>
            )
          }
        )}
      </VStack>
    )
  }

  const getBlockUrl = (pathItem?: SearchablePathItem) => {
    if (!pathItem) return ""

    let url = getStepUrl(resource.slug, cohort!.id, pathItem.source_id)

    const blockSuffix = `${BLOCK_HASH_PREFIX}${item.id}`
    url = `${url}#${blockSuffix}`

    if (getResultURL) {
      return getResultURL(pathItem as PathItemFragment, blockSuffix)
    }

    return url
  }

  return getIsPathItem(item) ? (
    <PWAdminHint model={item}>{renderItem(item)}</PWAdminHint>
  ) : (
    renderBlock(item)
  )
}

export default PathNavigatorItem
